import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import { Logo } from "./Logo";
import { LangToggle, useLang } from "../LanguageContext";
import { waLink } from "../data/site";

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { s } = useLang();

  const LINKS = [
    { to: "/", label: s.nav.home, id: "nav-home" },
    { to: "/accompagnements", label: s.nav.offers, id: "nav-offers" },
    { to: "/contact", label: s.nav.contact, id: "nav-contact" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <header
        data-testid="site-nav"
        className={`fixed inset-x-0 top-0 z-40 transition-all duration-500 ${
          scrolled ? "glass py-3" : "bg-transparent py-5"
        }`}
      >
        <div className="mx-auto flex w-full max-w-6xl items-center justify-between px-5 sm:px-8">
          <Link to="/" data-testid="nav-logo-link" aria-label={s.nav.logoAria}>
            <Logo />
          </Link>

          <nav className="hidden items-center gap-1 md:flex">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.id}
                className={({ isActive }) =>
                  `rounded-full px-5 py-2 text-sm font-medium tracking-wide transition-all duration-300 ${
                    isActive ? "text-gold-light" : "text-white/60 hover:text-white"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <LangToggle className="ml-2" />
            <a
              href={waLink(s.wa.defaultMsg)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-whatsapp-cta"
              className="btn-mint ml-3 !px-5 !py-2.5"
            >
              <MessageCircle className="h-4 w-4" />
              {s.nav.whatsapp}
            </a>
          </nav>

          <button
            className="glass rounded-full p-3 text-white md:hidden"
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? s.nav.closeMenu : s.nav.openMenu}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            className="fixed inset-0 z-30 flex flex-col items-center justify-center gap-7 bg-obsidian/95 backdrop-blur-xl md:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {LINKS.map((l, i) => (
              <motion.div
                key={l.to}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08 * i, duration: 0.5 }}
              >
                <NavLink
                  to={l.to}
                  data-testid={`${l.id}-mobile`}
                  className={({ isActive }) =>
                    `font-serif text-4xl ${isActive ? "text-gold-light italic" : "text-white/85"}`
                  }
                >
                  {l.label}
                </NavLink>
              </motion.div>
            ))}
            <LangToggle />
            <motion.a
              href={waLink(s.wa.defaultMsg)}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-mint mt-2"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
            >
              <MessageCircle className="h-4 w-4" /> {s.nav.whatsapp}
            </motion.a>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
